<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import {
	PageHeading,
	Input,
	Select,
	Checkbox,
	Icon,
	Dropdown,
	Pagination,
	Button,
	ProfilePicture,
} from "@components";

import { getPrice } from "@utils";
import moment from "moment";

import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

import { useTransaction } from "@store/transaction.js";
const $transaction = useTransaction();

import { useCustomer } from "@store/customer.js";
const $customer = useCustomer();

import { useSettings } from "@store/settings.js";
const $settings = useSettings();

const filter = ref({
	search: "",
	status: "all",
});

onMounted(async () => {
	$customer.fetchCustomers();
	await $transaction.fetchTransactions();
});

const filteredTransactions = computed(() => {
	let filteredArray = $transaction.getTransactions;
	if (!filteredArray) return [];

	// Filter by status
	if (filter.value.status && filter.value.status !== "all") {
		filteredArray = filteredArray.filter(
			(transaction) => transaction?.payment_status == filter.value.status
		);
	}

	// Filter by search
	if (filter.value.search) {
		filteredArray = filteredArray.filter((transaction) =>
			String(transaction.payment_amount)
				.toLowerCase()
				.includes(filter.value.search.toLowerCase())
		);
	}

	return filteredArray;
});

// // Get paginated transaction.
const currentPage = ref(1);
const perPage = ref(10);
const sortBy = ref("transaction_id");
const sortDirection = ref("DESC");

const getPaginatedTransactions = computed(() => {
	let _transactions = filteredTransactions.value;

	// Sort.
	_transactions = _transactions.sort((a, b) => {
		if (Number(a[sortBy.value]) < Number(b[sortBy.value])) {
			return sortDirection.value == "ASC" ? -1 : 1;
		}

		if (Number(a[sortBy.value]) > Number(b[sortBy.value])) {
			return sortDirection.value == "ASC" ? 1 : -1;
		}

		return 0;
	});

	return {
		total: _transactions.length,
		data: _transactions.slice(
			(currentPage.value - 1) * perPage.value,
			currentPage.value * perPage.value
		),
	};
});

const state = reactive({
	allCheckbox: false,
	hover: null,
});

const toggleAllTransactions = () => {
	// // Log($transaction.getTransactions)
	// $transaction.getTransactions.forEach(transaction => {
	//     // Assign the value of allCheckbox to each transaction checkbox
	//     transaction.meta.checkbox = state.allCheckbox ? true : false
	// })
	getPaginatedTransactions.value.data.forEach((transaction) => {
		return (transaction.meta.checkbox = state.allCheckbox);
	});
};

const getSelectedTransactions = computed(() => {
	return (
		getPaginatedTransactions.value.data.filter(
			(transaction) => transaction?.meta.checkbox == true
		) || []
	);
});

watch(getSelectedTransactions, (newValue) => {
	if (newValue.length == 0) {
		state.allCheckbox = false;
	} else if (
		newValue.length == perPage.value ||
		newValue.length == getPaginatedTransactions.value.data?.length
	) {
		state.allCheckbox = true;
	} else {
		state.allCheckbox = -1;
	}
});

const tableHoverRow = (row = null) => {
	if (typeof row === "number") {
		state.hover = row;
	} else {
		state.hover = null;
	}
};

const statusOptions = computed(() => {
	return [
		{ label: "All", value: "all" },
		{ label: "Paid", value: "paid" },
		{ label: "Unpaid", value: "unpaid" },
	];
});

const deleteTransaction = async (transaction) => {
	if (!transaction.transaction_id)
		return Toast.error(
			`No ${String($settings.labels.customer?.singular).toLowerCase()} id found`
		);

	const response = await transaction.delete(transaction.transaction_id);
	if (!response) return;

	if (response.success) {
		Toast.success(
			`${String(
				$settings.labels.payment?.singular
			).toLowerCase()} deleted successfully`
		);
		await $transaction.fetchTransactions();
	} else {
		Toast.error(response.message);
	}
};

const deleteSelected = async () => {
	let ids = getSelectedTransactions.value.map(
		(transaction) => transaction.transaction_id
	);

	let confirm = await Prompt.fire({
		title: `Delete ${String($settings.labels.payment?.plural).toLowerCase()}`,
		message: `Are you sure you want to delete selected ${
			ids.length > 1 ? ids.length : ""
		} ${String($settings.labels.payment?.singular).toLowerCase()}${
			ids.length > 1 ? "s" : ""
		}?`,
		ok: "Yes, delete",
		cancel: "No, cancel",
	});
	if (!confirm.ok) return;

	await $transaction.bulkDelete(ids);
	$transaction.fetchTransactions();
};
</script>

<template>
	<div class="my-[-28px] md:max-w-[992px] m-auto">
		<PageHeading :title="$settings.labels.payment?.plural" />
		<!-- Filter -->
		<div class="flex flex-col md:flex-row gap-3 md:gap-[52px] my-7 justify-between items-center w-full">
			<Input v-model="filter.search" class="w-full" :placeholder="`Search ${String($settings.labels.payment?.plural).toLowerCase()}`" iconLeft="searchLg"></Input>
			<Select :options="statusOptions" v-model="filter.status" placeholder="Status" class="w-full lg:w-[191px]"></Select>
		</div>

		<!-- Actions -->
		<Transition name="router-animation">
			<Button v-if="getSelectedTransactions.length" size="md" type="secondary_gray" radius="square" @click.prevent="deleteSelected()" class="mb-5 border-[#FDA29B] text-[#B42318]">Delete</Button>
		</Transition>

		<!-- transaction list -->
		<div class="flex overflow-scroll md:overflow-visible">
			<!-- ID column -->
			<div class="w-[98px]">
				<div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_1px]">
					<div class="h-[38px] flex items-center gap-3 py-2.5 px-4 bg-white text-sm font-medium text-[#667085]">
						<Checkbox v-if="access('transaction.delete')" v-model="state.allCheckbox" @click="toggleAllTransactions" />ID
					</div>
				</div>
				<div @mouseover="tableHoverRow(transaction.transaction_id)" @mouseleave="tableHoverRow" v-for="(transaction, index) in getPaginatedTransactions.data" :key="index" class="h-[56px] flex items-center gap-3 py-[18px] px-4 border border-[#EAECF0] border-y-0 last:border-b text-sm font-medium text-[#344054]" :class="{ 'bg-[#F9FAFB]': state.hover == transaction.transaction_id }">
					<Checkbox v-if="access('transaction.delete')" v-model="transaction.meta.checkbox"></Checkbox>
					{{ '#' + transaction.transaction_id }}
				</div>
			</div>

			<!-- Date column -->
			<div class="w-[120px]">
				<div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_0px_1px_0px]">
					<div class="h-[38px] flex items-center gap-3 py-2.5 px-4 bg-white text-sm font-medium text-[#667085]">
						<div>Date</div>
					</div>
				</div>
				<div @mouseover="tableHoverRow(transaction.transaction_id)" @mouseleave="tableHoverRow" v-for="(transaction, index) in getPaginatedTransactions.data" :key="index" class="h-[56px] py-[18px] px-4 border border-[#EAECF0] border-x-0 border-y-0 last:border-b text-sm font-medium text-[#344054] text-nowrap" :class="{ 'bg-[#F9FAFB]': state.hover == transaction.transaction_id }">{{ moment(transaction.created_at, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YYYY') }}</div>
			</div>

			<!-- Customer column -->
			<div class="w-[217px]">
				<div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_1px]">
					<div class="h-[38px] flex items-center gap-3 py-2.5 px-4 bg-white text-sm font-medium text-[#667085]">
						<div>{{ $settings.labels.customer?.singular }}</div>
					</div>
				</div>
				<div @mouseover="tableHoverRow(transaction.transaction_id)" @mouseleave="tableHoverRow" v-for="(transaction, index) in getPaginatedTransactions.data" :key="index" class="h-[56px] w-full flex gap-2 items-center py-[16px] px-4 border border-[#EAECF0] border-y-0 last:border-b text-[#344054] text-sm font-semibold text-nowrap" :class="{ 'bg-[#F9FAFB]': state.hover == transaction.transaction_id }">
					<!-- <img :src="$customer.findCustomerById(transaction.customer_id)?.meta?.profile_image_url ? $customer.findCustomerById(transaction.customer_id)?.meta?.profile_image_url : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'" class="w-[24px] h-[24px] object-cover rounded-full" /> -->
					<ProfilePicture :name="$customer.findCustomerById(transaction.customer_id)?.name" :img="$customer.findCustomerById(transaction.customer_id)?.meta?.profile_image_url" :bgColor="$customer.findCustomerById(transaction.customer_id)?.meta?.color" class="w-[24px] h-[24px]" textClass="!text-[12px] leading-[normal]" />
					<div class="truncate max-w-[calc(100%-32px)]" :title="$customer.findCustomerById(transaction.customer_id).name || 'N/A'">{{ $customer.findCustomerById(transaction.customer_id).name || 'N/A' }}</div>
				</div>
			</div>

			<!-- Amount column -->
			<div class="w-[160px]">
				<div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_0px_1px_0px]">
					<div class="h-[38px] flex items-center gap-3 py-2.5 px-4 bg-white text-sm font-medium text-[#667085]">
						<div>Amount</div>
					</div>
				</div>
				<div @mouseover="tableHoverRow(transaction.transaction_id)" @mouseleave="tableHoverRow" v-for="(transaction, index) in getPaginatedTransactions.data" :key="index" class="h-[56px] py-[18px] px-4 border border-[#EAECF0] border-x-0 border-y-0 last:border-b text-[#344054] text-sm font-medium" :class="{ 'bg-[#F9FAFB]': state.hover == transaction.transaction_id }">{{ getPrice(transaction.payment_amount) }}</div>
			</div>

			<!-- Method column -->
			<div class="w-[160px]">
				<div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_1px]">
					<div class="h-[38px] flex items-center gap-3 py-2.5 px-4 bg-white text-sm font-medium text-[#667085]">
						<div>Method</div>
					</div>
				</div>
				<div @mouseover="tableHoverRow(transaction.transaction_id)" @mouseleave="tableHoverRow" v-for="(transaction, index) in getPaginatedTransactions.data" :key="index" class="h-[56px] flex items-center gap-2 py-[16px] px-4 border border-[#EAECF0] border-y-0 last:border-b text-[#344054] text-sm font-medium" :class="{ 'bg-[#F9FAFB]': state.hover == transaction.transaction_id }">
					<Icon :name="transaction.payment_method == 'card' ? 'creditCard02' : transaction.payment_method == 'bank_transfer' ? 'bankNote01' : 'currencyDollarCircle'" size="24px" class="text-[#333333]" />
					<div class="capitalize">{{ String(transaction.payment_method).split('_').join(' ') }}</div>
				</div>
			</div>

			<!-- Status column -->
			<div class="w-[242px]">
				<div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_0px]">
					<div class="h-[38px] flex items-center gap-3 py-2.5 px-4 bg-white text-sm font-medium text-[#667085]">
						<div>Status</div>
					</div>
				</div>
				<div @mouseover="tableHoverRow(transaction.transaction_id)" @mouseleave="tableHoverRow" v-for="(transaction, index) in getPaginatedTransactions.data" :key="index" class="h-[56px] flex justify-between items-center py-[14px] px-4 border border-[#EAECF0] border-l-0 border-y-0 last:border-b text-[#344054] text-sm font-medium" :class="{ 'bg-[#F9FAFB]': state.hover == transaction.transaction_id }">
					<!-- <div class="text-sm font-medium py-0.5 px-2.5 rounded-2xl" :class="[transaction.payment_status == 'publish' ? 'text-[#027A48] bg-[#ECFDF3]' : 'text-[#7a1202] bg-[#fdecec]']">
                        {{ transaction.payment_status == 'publish' ? 'Paid' : transaction.payment_status }}
					</div>-->
					<div class="text-sm font-medium py-0.5 px-2.5 rounded-2xl w-fit capitalize" :class="{
                        'text-[#027A48] bg-[#ECFDF3]': transaction.payment_status == 'paid',
                        'text-[#B84646] bg-[#FEE2E2]': transaction.payment_status !== 'paid'
                    }">{{ transaction.payment_status || 'unpaid' }}</div>

					<!-- actions -->
					<div class="flex items-center gap-4">
						<router-link v-if="access('transaction.edit')" :to="{ name: 'transaction.edit', params: { transactionId: transaction.transaction_id }, query: { ...router.currentRoute.value.query } }" class="text-[#7F56D9] text-sm font-semibold">Details</router-link>

						<Dropdown contentClass="w-56 top-[35px] right-0 w-[117px] z-[9]">
							<Icon name="dotsVertical" size="28px" class="text-[#98A2B3] rounded hover:bg-[#F2F4F7]" />

							<template #content>
								<div class="flex flex-col gap-1">
									<div v-if="access('transaction.edit')" class="rounded-md mx-1.5 my-0.5 transition">
										<router-link :to="{ name: 'transaction.edit', params: { transactionId: transaction.transaction_id }, query: { ...router.currentRoute.value.query } }" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
											<Icon size="16px" name="edit05" />
											<div class="text-[#344054] text-sm font-medium">Edit</div>
										</router-link>
									</div>
									<div v-if="access('transaction.delete')" class="rounded-md mx-1.5 my-0.5 transition">
										<div @click="deleteTransaction(transaction)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
											<Icon size="16px" name="trash01" />
											<div class="text-[#344054] text-sm font-medium">Delete</div>
										</div>
									</div>
								</div>
							</template>
						</Dropdown>
					</div>
				</div>
			</div>
		</div>

		<div v-if="getPaginatedTransactions.total == 0" class="mt-2 text-center text-[#b84646]">
			<p class="p-4 my-4 italic text-center text-gray-700 border-gray-400 rounded-lg bg-gray-50">No {{ String($settings.labels.payment?.plural).toLowerCase() }} found</p>
		</div>

		<Pagination v-if="getPaginatedTransactions.total !== 0" class="pt-[20px]" v-model:currentPage="currentPage" :totalItems="getPaginatedTransactions.total" :itemsPerPage="perPage" @pageChange="(event) => console.log(event)"></Pagination>
	</div>
</template>